<script setup lang="ts">
import { onMounted, onUnmounted, ref, watchEffect } from "vue";
import { CSSAnimationName } from "@zyzgroup/core-common";
import {
  useSafeArea$,
  SupportedEaseNameToCubicBezier
} from "@zyzgroup/core-web";
import CSSAnimation, { type CSSAnimationOptions } from "./CSSAnimation.vue";

// for merge ce shadow-root styles
defineOptions({
  customOptions: { CSSAnimation },
  inheritAttrs: true
});

const props = withDefaults(
  defineProps<{
    action?: "open" | "close" | "static";
    name?: CSSAnimationName;
    timingFunction?: SupportedEaseNameToCubicBezier;
    duration?: number;
    delay?: number;
  }>(),
  {
    action: "static", // 默认：已 open
    name: "moveFromRight",
    timingFunction: "ease",
    duration: 300,
    delay: 0
  }
);

const emits = defineEmits<{
  (event: "pageOpenStart"): void;
  (event: "pageOpenEnd"): void;
  (event: "pageCloseStart"): void;
  (event: "pageCloseEnd"): void;
}>();

const r = ref();

const safeAreaBottom = ref("0px");
let safeArea$;
onMounted(() => {
  safeArea$ = useSafeArea$().subscribe((s) => {
    safeAreaBottom.value = s.bottom;
  });
});
onUnmounted(() => {
  safeArea$?.unsubscribe();
});

let action = "static";

const cssanimationstart = () => {
  switch (action) {
    case "open":
      emits("pageOpenStart");
      break;
    case "close":
      emits("pageCloseStart");
      break;
    default:
      // 忽略：其他组件触发的 cssanimationstart
      break;
  }
};

const cssanimationend = () => {
  switch (action) {
    case "open":
      emits("pageOpenEnd");
      break;
    case "close":
      emits("pageCloseEnd");
      break;
    default:
      // 忽略：其他组件触发的 cssanimationend
      break;
  }
  action = "static";
};

const open = (options?: CSSAnimationOptions) => {
  action = "open";
  r.value?.trigger(options);
};

const close = (options?: CSSAnimationOptions) => {
  action = "close";
  r.value?.triggerReverse(options);
};

watchEffect(() => {
  switch (props.action) {
    case "open":
      open();
      break;
    case "close":
      close();
      break;
    case "static":
    default:
      break;
  }
});

defineExpose({ open, close });
</script>

<template>
  <CSSAnimation
    ref="r"
    class="page"
    :class="props.name"
    :name="props.name"
    :timingFunction="props.timingFunction"
    :duration="props.duration"
    :delay="props.delay"
    @cssanimationstart="cssanimationstart"
    @cssanimationend="cssanimationend"
  >
    <div class="page-header">
      <slot name="header"></slot>
    </div>

    <div class="page-main">
      <div class="page-main-scroll">
        <slot></slot>
      </div>
    </div>

    <div
      class="page-footer"
      :style="{ paddingBottom: safeAreaBottom }"
    >
      <slot name="footer"></slot>
    </div>

    <div class="page-global">
      <slot name="global"></slot>
    </div>
  </CSSAnimation>
</template>

<style scoped>
.page {
  position: relative;
  width: 100%;
  height: 100%;
  box-sizing: border-box;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}
.page-header {
  z-index: 2;
  flex: 0 0 auto;
  width: 100%;
  box-sizing: border-box;
  position: relative;
}
.page-main {
  z-index: 1;
  flex: 1 1 auto;
  width: 100%;
  box-sizing: border-box;
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}
.page-main-scroll {
  flex: 1 1 auto;
  width: 100%;
  box-sizing: border-box;
  position: relative;
  overflow-y: scroll;
}
.page-footer {
  z-index: 2;
  flex: 0 0 auto;
  width: 100%;
  box-sizing: border-box;
  position: relative;
}
/*
.page-main-scroll > :nth-child(1) {
  min-height: 100%;
}
*/
</style>
